import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppPath } from '@app/app-path';
import { LocalStorageItem } from '@core/enums/local-storage-item';
import { AppConfig } from '@core/models/app-config';
import { Customer } from '@core/models/customer.model';
import { Message } from '@core/models/message.model';
import { Publisher } from '@core/models/product.model';
import { AppConfigService } from '@core/services/app-config.service';
import { AuthService, AuthStatus } from '@core/services/auth.service';
import { CustomerService } from '@core/services/customer.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { Logger } from '@core/services/logger.service';
import { MessageService } from '@core/services/message.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { TranslateService } from '@ngx-translate/core';
import { IndeterminateHorizontalProgressIndicatorComponent } from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import { OrderCreatorService } from '@shared/services/order-creator.service';
import { TermsAndConditionsService } from '@shared/services/terms-and-conditions.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, combineLatest, distinctUntilChanged } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'app-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

	private readonly COMPONENT_NAME = "DashboardComponent";

	private readonly _callingOut$ = new BehaviorSubject<boolean>(false);
	private _fullscreenProgressOverlayRef?: OverlayRef;

	customers: Customer[] = [];
	enableChatToPublishers: string[] | undefined = undefined;

	readonly recentCustomers$ = new BehaviorSubject<Customer[] | undefined>(undefined);

	private subs = new SubSink();
	constructor (
		public userProfilesService: UserProfilesService,
		private _alertService: UserAlertService,
		private _authService: AuthService,
		private _appConfigService: AppConfigService,
		private _customerService: CustomerService,
		private _localStorageService: LocalStorageService,
		private _logger: Logger,
		private _messageService: MessageService,
		private _orderCreator: OrderCreatorService,
		private _overlay: Overlay,
		private _router: Router,
		private _termsAndConditionsService: TermsAndConditionsService,
		private _translate: TranslateService) { }

	ngOnInit(): void {
		this.subs.add(
			this._appConfigService.config$.subscribe((appConfig: AppConfig | undefined) => {
				if (appConfig) {
					this.enableChatToPublishers = appConfig.enableChatToPublishers;
				}
			})
		);

		this._fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this.subs.add(
			this._callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					const componentRef = this._fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
					if (componentRef) {
						this._translate.get("APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE").subscribe((msg) => componentRef.instance.displayString = msg);
					}
				}
				else {
					this._fullscreenProgressOverlayRef?.detach();
				}
			}));

		this.subs.add(
			combineLatest([this.userProfilesService.userProfile$, this._authService.authStatus$]).subscribe({
				next: ([profile, authStatus]) => {

					if (profile && profile.id && authStatus) {
						this.showTermsAndConditionsOnFirstLogin(profile.id, authStatus);
					}

					if (profile) {
						const recentCustomersIds = this.userProfilesService.getRecentCustomersIds();
						if (recentCustomersIds && recentCustomersIds.length > 0) {
							this.subs.add(
								this._customerService.getClients(recentCustomersIds).subscribe({
									next: (customers: Customer[]) => {
										// re-arrange the customers according to the recentCustomersIds
										this.customers = [];
										for (const id of recentCustomersIds) {
											let customer: Customer | undefined = customers.find(c => c.id === id);
											if (!customer) {
												customer = profile?.customers?.find(c => c.id === id);
											}
											if (customer) {
												this.customers.push(customer);
											}
											else {
												this._logger.warning(this.COMPONENT_NAME, `Bug or misconfiguration: cannot find the customer by Id="${id}"`);
											}
										}

										this.recentCustomers$.next(this.customers);
									}
								}));
						}
						else {
							this.recentCustomers$.next([]);
						}
					}
				}
			}));
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	createOrder(): void {
		this._callingOut$.next(true);
		this.subs.add(
			this._orderCreator.init().subscribe({
				next: () => {
					this._callingOut$.next(false);
					this._orderCreator.startOrderCreationFlow();
				},
				error: (error) => {
					this._callingOut$.next(false);
					this._logger.error(this.COMPONENT_NAME, "createOrder() failed.");
					this._alertService.errorByCassError(error);
				}
			})
		);
	}

	showAllAdvertisers(): void {
		this._router.navigate([AppPath.Advertisers]);
	}

	messageClicked(arg: { message: Message, publisher: Publisher | undefined }): void {
		this._messageService.markMessageAsRead(arg.message.id);
		this._router.navigate(['/order'], { queryParams: { orderId: arg.message.orderId, messageId: arg.message.id } });
	}

	private showTermsAndConditionsOnFirstLogin(userId: string, authStatus: AuthStatus): void {
		if (userId && authStatus.isAuthenticated && !authStatus.lastLogin) {
			// to avoid showing terms and conditions till the user logs out and in, save the user id in the local memory after t&c dialog is shown; show the dialog only once
			const usersIdsValue = this._localStorageService.loadValue(LocalStorageItem.USERS_ACCEPTED_T_AND_C) || "[]";
			const usersIds = JSON.parse(usersIdsValue) as string[];

			if (!usersIds.includes(userId)) {
				this._termsAndConditionsService.displayTermsAndConditionsDialog(true);
				usersIds.push(userId);
				this._localStorageService.saveValue(LocalStorageItem.USERS_ACCEPTED_T_AND_C, JSON.stringify(usersIds));
			}
		}
	}
}
